import { Col, Divider, Row, Select, Skeleton } from "antd";
import { Fragment, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Layout from "../../Components/Layout/Layout";
import ProductSlider from "../../Components/Sliders/ProductSlider/ProductSlider";
import styles from "./ProductsByCategory.module.scss";
import filter_icon from "./images/filter.svg";
import { Input } from "antd";

import search_icon from "./images/search.svg";
import scan_icon from "./images/scan_icon.svg";
import ProductFilterDrawer from "../../Components/ProductFilterDrawer/ProductFilterDrawer";
import {
  useFetchCategories,
  useFetchDesktopFiltersState,
  useFetchDiscountedProducts,
  useFetchSubcategories,
  useFetchSubSubcategories,
} from "../../Hooks/useProductByCategoryData";
import { DesktopFilter } from "./subcomponents/DesktopFilter/DesktopFilter";
import { ProductsBySubSubcategoryListing } from "../../Components/ProductsBySubSubcategoryListing/ProductsBySubSubcategoryListing";
import useScrollPosition from "../../Hooks/useScrollPosition";
import { BreadcrumbHeader } from "./subcomponents/BreadcumbHeader/BreadcrumbHeader";
import { SubCategoriesHorizontalList } from "./subcomponents/SubCategoriesHorizontalList/SubCategoriesHorizontalList";
import { getTransformedProducts } from "../../Hooks/useProductsBySubCategory";

const ProductsByCategory = () => {
  const { slug } = useParams();
  const [sortBy, setSortBy] = useState("latest");
  const [filterBrands, setFilterBrands] = useState([]);
  const [filterNutriScore, setFilterNutriScore] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSection, setActiveSection] = useState(0);

  const [subCategory, subCategoryLoading] = useFetchSubcategories(slug);
  const [discountedProducts] = useFetchDiscountedProducts();
  const subSubCategories = useFetchSubSubcategories(slug);
  const [category] = useFetchCategories(subCategory?.category);
  const sectionsRef = useRef({});

  const scrollPosition = useScrollPosition();

  const [showMobileDrawer, setShowMobileDrawer] = useState(false);
  const [desktopFilterUIStates] = useFetchDesktopFiltersState(slug);

  const recentlyViewedProducts = window.localStorage.getItem("recentlyViewed")
    ? getTransformedProducts(JSON.parse(window.localStorage.getItem("recentlyViewed")))
    : [];

  const [desktopFilters, setDesktopFilters] = useState({
    discounts_and_offer: [],
    brands: [],
  });

  const renderBanner = () => {
    return (
      <div className="row">
        {subCategoryLoading ? (
          <div className={"custom_antd_image_skeleton"}>
            <Skeleton.Image
              paragraph={false}
              style={{ height: "181px" }}
              active={true}
            />
          </div>
        ) : (
          <>
            <div className="d-none d-lg-block">
              <img
                src={
                  subCategory.banner ??
                  "https://desi-fresh.b-cdn.net/misc/discount_cat_banner.png"
                }
                alt="discount"
                loading="lazy"
                className="img-fluid mt-2 rounded-3"
              />
            </div>
            <div className="d-block d-lg-none">
              <img
                src={
                  subCategory.mobile_banner ??
                  "https://desi-fresh.b-cdn.net/misc/discount_cat_banner_mobile.png"
                }
                alt="discount"
                loading="lazy"
                className="img-fluid rounded-3"
              />
            </div>
          </>
        )}
      </div>
    );
  };

  const renderMobileFilter = () => {
    return (
      <div className={styles.mobile_title_filter}>
        <Input
          placeholder="Find it fast with our search!"
          onChange={(e) => {
            setSearchTerm(e.target.value);
          }}
          value={searchTerm}
          prefix={<img src={search_icon} alt="search_icon" />}
          suffix={
            <>
              <img src={scan_icon} alt="scan_icon" />
              <img
                src={filter_icon}
                onClick={() => setShowMobileDrawer(true)}
                alt="filter_icon"
                className={styles.filter_icon}
              />
            </>
          }
        />
      </div>
    );
  };

  useEffect(() => {
    const brands = desktopFilters.brands.map((brand) => brand.split("/")[1]);
    setFilterBrands(brands);
  }, [desktopFilters]);

  // defining resetFilter function that will reset all the filters in the drawer
  const resetFilter = () => {
    setFilterBrands([]);
    setFilterNutriScore([]);
  };

  const scrollToItem = (index) => {
    const element = sectionsRef.current[index];
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  // IntersectionObserver to track visible sections
  useEffect(() => {
    let timeoutId = null;
    const currentSections = sectionsRef.current;

    const observer = new IntersectionObserver(
      (entries) => {
        // Find the entry with the highest intersection ratio
        let maxEntry = null;
        let maxRatio = 0;

        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > maxRatio) {
            maxRatio = entry.intersectionRatio;
            maxEntry = entry;
          }
        });

        if (maxEntry) {
          // Debounce the state update
          if (timeoutId) {
            clearTimeout(timeoutId);
          }

          timeoutId = setTimeout(() => {
            const index = Object.keys(currentSections).find(
              (key) => currentSections[key] === maxEntry.target,
            );
            if (index !== undefined) {
              setActiveSection(Number(index));
            }
          }, 100);
        }
      },
      {
        root: null,
        rootMargin: "-100px 0px -200px 0px",
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5],
      },
    );

    Object.values(currentSections).forEach((section) => {
      if (section) {
        observer.observe(section);
      }
    });

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      Object.values(currentSections).forEach((section) => {
        if (section) {
          observer.unobserve(section);
        }
      });
    };
  }, [subSubCategories]);

  // sliding and scrolling management ends here

  return (
    <Layout>
      <div className={styles.products}>
        <div className="container">
          <BreadcrumbHeader category={category} subCategory={subCategory} />

          {renderBanner()}

          <div
            className={
              scrollPosition < 50
                ? styles.title_and_filter
                : styles.title_and_filter_scrolled
            }
          >
            <Row style={{ rowGap: 0 }} gutter={[10, 10]}>
              <Col lg={20} xs={0}>
                <DesktopFilter
                  desktopFilters={desktopFilters}
                  setDesktopFilters={setDesktopFilters}
                  desktopFilterUIStates={desktopFilterUIStates}
                />
              </Col>
              <Col lg={4} xs={0}>
                <div
                  className={`${styles.filter_products} justify-content-end`}
                >
                  <label htmlFor="articles">Sort by</label>
                  <Select value={sortBy} onChange={(value) => setSortBy(value)}>
                    <Select.Option value="latest">Latest Items</Select.Option>
                    <Select.Option value="popular">Most Popular</Select.Option>
                  </Select>
                </div>
              </Col>
              <Col lg={10} xs={24}>
                <div className={styles.title}>{subCategory.name}</div>

                {renderMobileFilter()}

                <ProductFilterDrawer
                  resetFilter={resetFilter}
                  desktopFilters={desktopFilters}
                  showMobileDrawer={showMobileDrawer}
                  setShowMobileDrawer={setShowMobileDrawer}
                  setDesktopFilters={setDesktopFilters}
                  desktopFilterUIStates={desktopFilterUIStates}
                />
              </Col>
              <Col lg={10} xs={24}>
                <SubCategoriesHorizontalList
                  subSubCategories={subSubCategories}
                  onChangeSelection={scrollToItem}
                  activeSection={activeSection}
                />
              </Col>
            </Row>
          </div>

          {subSubCategories.map((subSubCategory, index) => (
            <div
              key={String(subSubCategory.id)}
              ref={(el) => (sectionsRef.current[index] = el)}
              className={
                scrollPosition < 50
                  ? styles.products_subcategory_listing
                  : styles.products_subcategory_listing_scrolled
              }
            >
              <ProductsBySubSubcategoryListing
                subcategory={slug.split("_")[1]}
                filterNutriScore={filterNutriScore}
                filterBrands={filterBrands}
                query={searchTerm}
                desktopFilters={desktopFilters}
                sortBy={sortBy}
                subSubCategory={subSubCategory}
              />
            </div>
          ))}

          <Divider style={{ margin: 0 }} />

          {/* recommended product slider */}
          <Row style={{ margin: "1rem 0" }}>
            <Col lg={24} xs={24}>
              <ProductSlider
                showDiscountVariants
                products={discountedProducts}
                title="Discount & Offers"
                link="/"
              />
            </Col>
          </Row>

          <div className="dt_only container">
            <Divider />
          </div>

          {/* recently viewed slider */}
          {recentlyViewedProducts.length > 0 && (
            <Row style={{ margin: "1rem 0" }}>
              <Col lg={24} xs={24}>
                <ProductSlider
                  products={recentlyViewedProducts}
                  title="Recently Viewed"
                  link="/"
                />
              </Col>
            </Row>
          )}
        </div>

        <div className="dt_only container">
          <Divider style={{ margin: 0 }} />
        </div>
      </div>
    </Layout>
  );
};

export default ProductsByCategory;
